import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { aiVisibilityService } from '../../services/aiVisibilityService';
import StatusScreen, { StatusText } from '../../components/StatusScreen';
import { CONTACT_EMAIL, contactHref } from '../../utils/contact';
import ReportView from './components/ReportView';

const POLL_MS = 5000;

const MESSAGES = {
  loading: { title: 'Cargando tu informe…', body: null },
  generating: {
    title: 'Estamos preparando tu informe',
    body: 'Suele tardar un par de minutos y esta página se actualiza sola. Guarda el enlace: es el de tu informe.'
  },
  failed: {
    title: 'No hemos podido generar tu informe',
    body: 'Algo ha fallado al prepararlo. Si usaste un código de invitación, no se ha gastado: vuelve a intentarlo en unos minutos.'
  },
  failed_paid: {
    title: 'No hemos podido generar tu informe',
    body: 'Algo ha fallado al prepararlo. Tu pago sigue valiendo: vuelve a intentarlo y lo preparamos de nuevo.'
  },
  not_found: {
    title: 'No encontramos este informe',
    body: 'Revisa que el enlace esté completo, tal como te llegó por email.'
  },
  unavailable: {
    title: 'El servicio no está disponible ahora mismo',
    body: 'Inténtalo de nuevo en unos minutos.'
  }
};

// A paid report, read by its (unguessable) id through the get_ai_report RPC.
// After a card payment the address also carries ?pago=<Stripe session id>:
// if the report fails, the same payment can start it again.
const ReportPage = () => {
  const { reportId } = useParams();
  const [params] = useSearchParams();
  const paymentSession = params.get('pago');
  const navigate = useNavigate();
  const [state, setState] = useState({ status: 'loading', report: null });
  const [retry, setRetry] = useState({ busy: false, error: null });

  useEffect(() => {
    let cancelled = false;
    let timer;

    const load = async () => {
      if (!supabase) {
        setState({ status: 'unavailable', report: null });
        return;
      }
      const { data, error } = await supabase.rpc('get_ai_report', { report_id: reportId });
      if (cancelled) return;

      if (error) {
        // An id that isn't a valid UUID errors out; treat it as not found.
        setState({ status: error.code === '22P02' ? 'not_found' : 'unavailable', report: null });
      } else if (!data) {
        setState({ status: 'not_found', report: null });
      } else if (data.status === 'ready' && data.report) {
        setState({ status: 'ready', report: data.report });
      } else if (data.status === 'failed') {
        setState({ status: 'failed', report: null });
      } else {
        setState({ status: 'generating', report: null });
        timer = setTimeout(load, POLL_MS);
      }
    };

    setState({ status: 'loading', report: null });
    load();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [reportId]);

  const retryPaidReport = async () => {
    setRetry({ busy: true, error: null });
    const result = await aiVisibilityService.confirmPayment(paymentSession);
    if (result.success && result.reportId !== reportId) {
      setRetry({ busy: false, error: null });
      navigate(`/informe/${result.reportId}?pago=${encodeURIComponent(paymentSession)}`, { replace: true });
    } else {
      setRetry({ busy: false, error: result.message || 'No hemos podido volver a empezar tu informe.' });
    }
  };

  if (state.status === 'ready') return <ReportView report={state.report} reportId={reportId} />;

  const paidFailure = state.status === 'failed' && Boolean(paymentSession);
  const message = MESSAGES[paidFailure ? 'failed_paid' : state.status];
  const mailto =
    state.status === 'failed'
      ? contactHref('Mi informe de webspector', `Informe: ${reportId}${paymentSession ? `\nPago: ${paymentSession}` : ''}`)
      : null;

  return (
    <StatusScreen kicker="Informe GEO · Visibilidad en IA" title={message.title} busy={state.status === 'generating'}>
      {message.body && <StatusText>{message.body}</StatusText>}
      {paidFailure && (
        <div className="mt-10">
          <button type="button" className="lp-btn" onClick={retryPaidReport} disabled={retry.busy}>
            {retry.busy ? 'Empezando de nuevo…' : 'Volver a intentarlo'}
          </button>
          {retry.error && (
            <p role="alert" className="mt-4 text-sm font-medium text-[var(--lp-alarm)]">
              {retry.error}
            </p>
          )}
        </div>
      )}
      {mailto && (
        <StatusText>
          Si el problema sigue, escríbenos a{' '}
          <a href={mailto} className="text-[var(--lp-text)] underline underline-offset-4">
            {CONTACT_EMAIL}
          </a>
          .
        </StatusText>
      )}
    </StatusScreen>
  );
};

export default ReportPage;
